import { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  MenuButton,
  Dropdown,
} from '@mui/joy';
import {
  CaretRight as ChevronRight,
  CaretDown as ExpandMore,
  DotsThreeVertical as MoreVert,
  Plus as Add,
  Trash as Delete,
} from '@phosphor-icons/react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableTreeItem } from './SortableTreeItem';
import type { PageTreeNode, PageReorderItem } from '../../../types/pages';

interface SidebarTreeProps {
  tree: PageTreeNode[];
  selectedPageId: string | null;
  onSelectPage: (pageId: string) => void;
  onCreatePage: (parentId?: string) => void;
  onDeletePage: (pageId: string) => void;
  onReorderPages: (items: PageReorderItem[]) => void;
}

export function SidebarTree({
  tree,
  selectedPageId,
  onSelectPage,
  onCreatePage,
  onDeletePage,
  onReorderPages,
}: SidebarTreeProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [draggedItem, setDraggedItem] = useState<PageTreeNode | null>(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const toggleExpanded = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const flattenTree = (nodes: PageTreeNode[], parentId: string | null = null): PageReorderItem[] => {
    const result: PageReorderItem[] = [];
    nodes.forEach((node, index) => {
      result.push({
        id: node.id,
        parent: parentId,
        position: index,
      });
      if (node.children && node.children.length > 0) {
        result.push(...flattenTree(node.children, node.id));
      }
    });
    return result;
  };

  const findNodeById = (nodes: PageTreeNode[], id: string): PageTreeNode | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    const draggedNode = findNodeById(tree, event.active.id as string);
    setDraggedItem(draggedNode);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggedItem(null);
    
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }
    
    const draggedId = active.id as string;
    const targetId = over.id as string;
    
    console.log('=== DRAG OPERATION ===');
    console.log('Dragged ID:', draggedId);
    console.log('Target ID:', targetId);
    console.log('Original tree:', tree);
    
    // Simpler approach: work with a flat list of root items only for now
    // (This handles the common case of reordering at the root level)
    const rootItems = [...tree];
    
    // Find indices
    const draggedIndex = rootItems.findIndex(item => item.id === draggedId);
    const targetIndex = rootItems.findIndex(item => item.id === targetId);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      console.log('Could not find dragged or target item in root level');
      return;
    }
    
    console.log('Dragged index:', draggedIndex, 'Target index:', targetIndex);
    
    // Remove the dragged item
    const [draggedItem] = rootItems.splice(draggedIndex, 1);
    
    // Insert after the target (or adjust based on direction)
    let newIndex = targetIndex;
    if (draggedIndex < targetIndex) {
      // If dragging down, the target index doesn't change after removal
      newIndex = targetIndex;
    } else {
      // If dragging up, insert after the target
      newIndex = targetIndex + 1;
    }
    
    rootItems.splice(newIndex, 0, draggedItem);
    
    console.log('Reordered items:', rootItems.map(item => item.title));
    
    // Create the reorder data with new positions
    const reorderData = rootItems.map((item, index) => ({
      id: item.id,
      parent: null, // Root level items
      position: index,
    }));
    
    console.log('Sending reorder data:', reorderData);
    
    // Send the reordered items to the backend
    onReorderPages(reorderData);
  };

  const renderTreeNode = (node: PageTreeNode, depth: number = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = selectedPageId === node.id;

    return (
      <Box key={node.id}>
        <SortableTreeItem id={node.id}>
          <ListItem
            sx={{
              pl: depth * 2,
              backgroundColor: isSelected ? 'background.level1' : 'transparent',
            }}
          >
            <IconButton
              size="sm"
              variant="plain"
              sx={{ visibility: hasChildren ? 'visible' : 'hidden' }}
              onClick={() => toggleExpanded(node.id)}
            >
              {isExpanded ? <ExpandMore /> : <ChevronRight />}
            </IconButton>
            
            <ListItemButton
              onClick={() => onSelectPage(node.id)}
              sx={{ flex: 1 }}
            >
              <ListItemContent>
                <Typography level="body-sm">{node.title}</Typography>
              </ListItemContent>
            </ListItemButton>

            <Dropdown>
              <MenuButton
                slots={{ root: IconButton }}
                slotProps={{
                  root: { variant: 'plain', size: 'sm' },
                }}
              >
                <MoreVert />
              </MenuButton>
              <Menu placement="bottom-end" size="sm">
                <MenuItem onClick={() => onCreatePage(node.id)}>
                  <Add size={18} style={{ marginRight: 8 }} />
                  Add Child Page
                </MenuItem>
                <MenuItem onClick={() => onDeletePage(node.id)}>
                  <Delete size={18} style={{ marginRight: 8 }} />
                  Delete
                </MenuItem>
              </Menu>
            </Dropdown>
          </ListItem>
        </SortableTreeItem>

        {hasChildren && isExpanded && (
          <Box>
            {node.children.map((child) => renderTreeNode(child, depth + 1))}
          </Box>
        )}
      </Box>
    );
  };

  // Get all page IDs for sortable context
  const getAllPageIds = (nodes: PageTreeNode[]): string[] => {
    const ids: string[] = [];
    const traverse = (nodeList: PageTreeNode[]) => {
      for (const node of nodeList) {
        ids.push(node.id);
        if (node.children) {
          traverse(node.children);
        }
      }
    };
    traverse(nodes);
    return ids;
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={getAllPageIds(tree)}
        strategy={verticalListSortingStrategy}
      >
        <List size="sm" sx={{ '--List-padding': '0px' }}>
          {tree.map((node) => renderTreeNode(node))}
          
          {tree.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography level="body-sm">No pages yet</Typography>
            </Box>
          )}
        </List>
      </SortableContext>
      
      <DragOverlay>
        {draggedItem && (
          <Box
            sx={{
              p: 1,
              backgroundColor: 'background.surface',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 'sm',
              boxShadow: 'lg',
            }}
          >
            <Typography level="body-sm">{draggedItem.title}</Typography>
          </Box>
        )}
      </DragOverlay>
    </DndContext>
  );
}